import React, { useRef, useState, useEffect } from 'react';
import { isEqual, get } from 'lodash';
import { useTheme, ITheme } from 'src/theme';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import IconDown from '@assets/icon/chevron-down-white.svg';
import IconUp from '@assets/icon/chevron-up-white.svg';
import Heading from '@components/Heading';
import Text from '@components/Text';
import Link from '@components/Link';

const TotalContainer = styled.div<StyledProps>`
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin-bottom: ${props => props.isOpen ? 20 : 0}px;
  .title-text {
    @media screen and (min-width: 768px) {
      font-weight: 300;
    }
  }
`

const ToggleContainer = styled.div<StyledProps>`
  width: 100%;
  padding: 0 20px;
  margin-bottom: ${props => props.isOpen ? '20px' : '0px'};
  overflow: hidden;
  height: 0;
  transition: height ease 0.2s;
`;

const open = css`
  height: auto;
`;

const overideIcon = css`
  img {
    width: 12px;
    height: 12px;
    top: 2px;
  }
`;

const lineThrough = css`
  text-decoration: line-through;
`;

const Row = styled.div<StyledProps>`
  padding: ${props => props.padding ? props.padding : '0 0 15px'};
  margin: ${props => props.margin ? props.margin : '0px'};
  border-bottom: 1px solid ${props => props.theme.colors.white};
  border-top: 1px solid ${props => props.theme.colors.white};
  border-bottom-width: ${props => props.borderBotWidth ? `${props.borderBotWidth}px` : '0px'};
  border-top-width: ${props => props.borderTopWidth ? `${props.borderTopWidth}px` : '0px'};
  display: flex;
  justify-content: space-between;
`;


const Container = styled.div<StyledProps>`
  background-color: ${props => props.theme.colors.secondary};
  padding: 15px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media screen and (min-width: 768px) {
    ${ToggleContainer} {
      max-width: 330px;
    }
  }
`

interface StyledProps {
  theme: ITheme;
  isOpen?: boolean;
  height?: string;
  borderTopWidth?: number;
  borderBotWidth?: number;
  padding?: string;
  margin?: string;
}

interface IValue {
  text: string;
  value: string | number;
  type?: string;
  promotions: { text: string, value: string }[];
  hasDiscount?: boolean
}
export interface PremiumCostProps {
  theme?: ITheme,
  totalPremium: IValue;
  details: IValue[],
  paymentFrequency: IValue,
  onToggle?: (e: any) => void;
}

function PremiumCost({ totalPremium, details, paymentFrequency, onToggle }: PremiumCostProps) {
  const theme = useTheme();
  const [totalValue, setTotalValue] = useState<IValue>(totalPremium);
  const [detailsValue, setDetailsValue] = useState<IValue[]>(details);
  const [frequencyValue, setFrequency] = useState<IValue>(paymentFrequency);

  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [height, setHeight] = useState<string>('0px');

  const toggle = () => {
    setHeight(!isOpen ? contentRef.current?.scrollHeight + 'px' : '0px');
    setIsOpen(!isOpen);
    if (onToggle) {
      onToggle(!isOpen);
    }
  }

  useEffect(() => {
    if (!isEqual(totalPremium, totalValue)) {
      setTotalValue(totalPremium);
    }

    if (!isEqual(detailsValue, details)) {
      setDetailsValue(details);
    }

    if (!isEqual(frequencyValue, paymentFrequency)) {
      setFrequency(paymentFrequency);
    }

  }, [totalPremium, details, paymentFrequency, totalValue, detailsValue, frequencyValue]);

  return (
    <Container theme={theme} >
      <TotalContainer theme={theme} isOpen={isOpen}>
        <Heading variant="default" margin="0 10px 0 0" color={theme.colors.white}>
          {totalValue.value}
        </Heading>
        <Text className="title-text" color={theme.colors.white} fontSizeTablet={theme.fontSizeTablet.smallTitle}>{totalValue.text}</Text>
      </TotalContainer>
      <ToggleContainer
        theme={theme}
        isOpen={isOpen}
        className={isOpen ? open : ''}
        ref={contentRef}
        style={{ height }}
      >
        {detailsValue.map((detail: IValue, index: number) => {
          return (<React.Fragment key={`${index}`}>
            {detail && detail.value && (
              <Row theme={theme} padding={get(detail, 'promotions', []).length > 0 ? '0 0 15px' : '0 0 15px'}>
                <Text color={theme.colors.white} fontWeight={get(detail, 'type', '') === 'total' ? 'bold' : 'normal'}>{detail?.text}</Text>
                <Text className={detail?.hasDiscount ? lineThrough : ''} fontWeight="bold" color={theme.colors.white}>{detail?.value}</Text>
              </Row>
            )}
            {get(detail, 'promotions', []).map((promotion: any, i: number) => (
              <Row key={`${index}-${i}`} theme={theme} padding={i === detail.promotions.length - 1 ? '0 0 15px' : '0px'}>
                <Text color={theme.colors.white}>{get(promotion, 'text' )}</Text>
                <Text fontWeight="bold" color={theme.colors.white}>{get(promotion, 'value')}</Text>
              </Row>))}
          </React.Fragment>);
        })}
        <Row theme={theme} borderBotWidth={2} borderTopWidth={1} padding="15px 0" margin={"0px"}>
          <Text color={theme.colors.white}>Payment frequency</Text>
          <Text fontWeight="bold" color={theme.colors.white}>{paymentFrequency.value}</Text>
        </Row>
      </ToggleContainer>
      <Link
        onClick={toggle}
        variant="button"
        fontWeight="normal"
        text="Premium Breakdown"
        icon={isOpen ? IconUp : IconDown}
        slotIcon="end"
        color={theme.colors.white}
        underlineStyle="dotted"
        className={overideIcon}
      />
    </Container>
  );
};

PremiumCost.defaultProps = {
  totalPremium: { text: 'total annual premium', value: 'S$0' },
  details: [],
  paymentFrequency: { text: '', value: '' }
}

export default PremiumCost;
